import { Helmet } from "react-helmet-async";
import React, { useEffect, useState } from "react";
import { getSiteSetting, listPlayers, listStaff, listMatches, getPlayerStats, getPlayerProfile } from "../lib/db";
import { Trophy, Calendar, MapPin, X, Shirt, Clock, Ruler, Weight, Footprints, Star, Heart } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useSiteImage } from "../context/SiteImagesContext";
import CalendarioWidget from "../components/CalendarioWidget";

const PILL_COLORS = {
  blue: { icon: "text-blue-400", border: "border-blue-500/40" },
  emerald: { icon: "text-emerald-400", border: "border-emerald-500/40" },
  purple: { icon: "text-purple-400", border: "border-purple-500/40" },
  orange: { icon: "text-orange-400", border: "border-orange-500/40" },
  yellow: { icon: "text-yellow-400", border: "border-yellow-500/40" },
  rose: { icon: "text-rose-400", border: "border-rose-500/40" },
  pink: { icon: "text-pink-400", border: "border-pink-500/40" },
  zinc: { icon: "text-zinc-500", border: "border-zinc-800" },
};

function InfoPill({ icon: Icon, label, value, color = "zinc" }) {
  if (!value && value !== 0) return null;
  const c = PILL_COLORS[color] || PILL_COLORS.zinc;
  return (
    <div className={`rounded-xl bg-zinc-950 border border-l-4 p-3 ${c.border}`}>
      <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 uppercase tracking-wider mb-1">
        {Icon && <Icon className={`h-3 w-3 ${c.icon}`} />} {label}
      </div>
      <div className="text-sm font-semibold text-zinc-100">{value}</div>
    </div>
  );
}

const ROLE_STYLES = {
  "Portiere": { gradient: "from-yellow-500 via-amber-500 to-yellow-600", ring: "ring-yellow-400/70", badge: "bg-yellow-400 text-zinc-950" },
  "Difensore": { gradient: "from-blue-500 via-blue-600 to-indigo-700", ring: "ring-blue-400/70", badge: "bg-blue-500 text-white" },
  "Centrocampista": { gradient: "from-emerald-500 via-emerald-600 to-teal-700", ring: "ring-emerald-400/70", badge: "bg-emerald-500 text-white" },
  "Attaccante": { gradient: "from-rose-500 via-red-600 to-orange-600", ring: "ring-rose-400/70", badge: "bg-rose-500 text-white" },
};
const DEFAULT_ROLE_STYLE = { gradient: "from-zinc-600 via-zinc-700 to-zinc-800", ring: "ring-zinc-500/60", badge: "bg-zinc-700 text-white" };

function fmtDataNascita(d) {
  if (!d) return null;
  return new Date(d + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
}

function PlayerStatsModal({ player, onClose }) {
  const [stats, setStats] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(!!player.persona_id);

  useEffect(() => {
    if (!player.persona_id) { setStats([]); setProfile(null); setLoading(false); return; }
    let active = true;
    setLoading(true);
    Promise.all([
      getPlayerStats(player.persona_id).catch(() => []),
      getPlayerProfile(player.persona_id).catch(() => null),
    ]).then(([statsData, profileData]) => {
      if (!active) return;
      setStats(statsData);
      setProfile(profileData);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [player.persona_id]);

  // Chiudi con ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // La scheda usa prima i dati inseriti direttamente sul giocatore;
  // se collegato al gestionale, li completa con quelli del profilo.
  const ruoloSecondario = player.secondary_role || profile?.ruolo_secondario;
  const piedePreferito = player.preferred_foot || profile?.piede_preferito;
  const altezza = player.height_cm || profile?.altezza_cm;
  const peso = player.weight_kg || profile?.peso_kg;
  const pregi = player.hobbies || profile?.pregi;
  const ispirazione = player.favorite_player || profile?.giocatore_ispirazione;
  const squadraCuore = player.favorite_team || profile?.squadra_cuore;
  const bio = player.bio;
  const dataNascita = fmtDataNascita(player.birth_date);

  const hasScheda = ruoloSecondario || piedePreferito || altezza || peso || pregi || ispirazione || squadraCuore || dataNascita;
  const roleStyle = ROLE_STYLES[player.role] || DEFAULT_ROLE_STYLE;

  const partiteGiocate = stats.filter((s) => s.minuti_giocati > 0).length;
  const totalGoal = stats.reduce((sum, s) => sum + (s.goal || 0), 0);
  const totalAmm = stats.reduce((sum, s) => sum + (s.ammonizioni || 0), 0);
  const totalEsp = stats.reduce((sum, s) => sum + (s.espulsioni || 0), 0);
  const ultimePartite = stats.slice(0, 5);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-zinc-900 border border-zinc-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 rounded-full bg-zinc-950/70 p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
          aria-label="Chiudi"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="relative flex items-center gap-4 p-6 border-b border-zinc-800 overflow-hidden">
          <div className={`absolute inset-0 bg-gradient-to-br ${roleStyle.gradient} opacity-[0.15]`} />
          <div className={`relative h-20 w-20 rounded-xl overflow-hidden bg-zinc-950 flex-shrink-0 ring-2 ${roleStyle.ring}`}>
            <img src={player.image} alt={player.name} className="h-full w-full object-cover" />
            {player.shirt_number != null && (
              <div className={`absolute -bottom-1 -right-1 h-7 w-7 rounded-full flex items-center justify-center text-xs font-display font-black border-2 border-zinc-900 ${roleStyle.badge}`}>
                {player.shirt_number}
              </div>
            )}
          </div>
          <div className="relative">
            <div className={`inline-block text-[10px] font-display font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-1.5 ${roleStyle.badge}`}>
              {player.role}
            </div>
            <div className="font-display font-black text-xl leading-tight">{player.name}</div>
            {player.shirt_number != null && (
              <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                <Shirt className="h-3 w-3" /> N. {player.shirt_number}
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <p className="text-zinc-500 text-sm">Caricamento...</p>
          ) : (
            <>
              {bio && (
                <p className="text-sm text-zinc-300 leading-relaxed mb-6">{bio}</p>
              )}

              {hasScheda && (
                <div className="mb-8">
                  <h4 className="text-overline text-zinc-500 mb-3">// Scheda giocatore</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <InfoPill icon={Calendar} label="Data di nascita" value={dataNascita} color="blue" />
                    <InfoPill label="Ruolo secondario" value={ruoloSecondario} color="purple" />
                    <InfoPill icon={Footprints} label="Piede preferito" value={piedePreferito} color="emerald" />
                    <InfoPill icon={Ruler} label="Altezza" value={altezza ? `${altezza} cm` : null} color="orange" />
                    <InfoPill icon={Weight} label="Peso" value={peso ? `${peso} kg` : null} color="orange" />
                  </div>
                  {(pregi || ispirazione || squadraCuore) && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                      <InfoPill icon={Star} label="Pregi" value={pregi} color="yellow" />
                      <InfoPill icon={Trophy} label="Giocatore a cui si ispira" value={ispirazione} color="rose" />
                      <InfoPill icon={Heart} label="Squadra del cuore" value={squadraCuore} color="pink" />
                    </div>
                  )}
                </div>
              )}

              {player.persona_id && (
                <>
                  <div className="grid grid-cols-4 gap-3 mb-8">
                    <div className="rounded-xl bg-zinc-950 border border-zinc-800 p-3 text-center">
                      <div className="font-display font-black text-2xl">{partiteGiocate}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">Partite</div>
                    </div>
                    <div className="rounded-xl bg-zinc-950 border border-zinc-800 p-3 text-center">
                      <div className="font-display font-black text-2xl text-yellow-400">{totalGoal}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">Gol</div>
                    </div>
                    <div className="rounded-xl bg-zinc-950 border border-zinc-800 p-3 text-center">
                      <div className="font-display font-black text-2xl">{totalAmm}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">🟨</div>
                    </div>
                    <div className="rounded-xl bg-zinc-950 border border-zinc-800 p-3 text-center">
                      <div className="font-display font-black text-2xl">{totalEsp}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">🟥</div>
                    </div>
                  </div>

                  {ultimePartite.length > 0 && (
                    <div>
                      <h4 className="text-overline text-zinc-500 mb-3">// Ultime partite</h4>
                      <div className="space-y-2">
                        {ultimePartite.map((s) => {
                          const m = s.partita;
                          if (!m) return null;
                          return (
                            <div key={s.id} className="flex items-center justify-between rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2 text-xs">
                              <div>
                                <div className="font-semibold text-zinc-200">
                                  {m.casa_trasferta} vs {m.avversario}
                                </div>
                                <div className="text-zinc-500 mt-0.5">
                                  {new Date(m.data).toLocaleDateString("it-IT")}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-zinc-400">
                                {s.minuti_giocati > 0 && (
                                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.minuti_giocati}'</span>
                                )}
                                {s.goal > 0 && <span className="text-yellow-400">⚽ {s.goal}</span>}
                                {s.ammonizioni > 0 && <span>🟨</span>}
                                {s.espulsioni > 0 && <span>🟥</span>}
                                <span className="font-bold text-zinc-300">{m.gol_fatti}-{m.gol_subiti}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {stats.length === 0 && (
                    <p className="text-zinc-500 text-sm">Nessuna statistica di partita disponibile per questo giocatore.</p>
                  )}
                </>
              )}

              {!hasScheda && !bio && !player.persona_id && (
                <p className="text-zinc-500 text-sm">Scheda in aggiornamento.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Squadra() {
  const [stagione, setStagione] = useState("2026/27");
  const [players, setPlayers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const heroImg = useSiteImage("squadra_hero", "https://images.unsplash.com/photo-1518609878373-06d740f60d8b?w=1600&q=80");

  useEffect(() => {
    getSiteSetting("stagione_corrente").then((d) => {
      if (d) setStagione(d);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    listPlayers().then(setPlayers).catch(() => {});
    listStaff().then(setStaff).catch(() => {});
    listMatches().then(setMatches).catch(() => {});
  }, []);

  const upcoming = matches.filter((m) => m.status === "scheduled");
  const played = matches.filter((m) => m.status === "played").reverse();

  const ORDINE_RUOLI_GIOCATORI = { "Portiere": 1, "Difensore": 2, "Centrocampista": 3, "Attaccante": 4 };
  const playersOrdinati = [...players].sort((a, b) => {
    const ra = ORDINE_RUOLI_GIOCATORI[a.role] || 99;
    const rb = ORDINE_RUOLI_GIOCATORI[b.role] || 99;
    if (ra !== rb) return ra - rb;
    return (a.shirt_number || 999) - (b.shirt_number || 999);
  });

  return (
    <div data-testid="squadra-page">
      <Helmet>
  <title>La Squadra – ASD Rubiana Calcio</title>
  <meta name="description" content="La rosa della prima squadra dell'ASD Rubiana Calcio. Giocatori, ruoli e statistiche della stagione in corso nel girone A di Terza Categoria di Pinerolo." />
  <meta property="og:title" content="La Squadra – ASD Rubiana Calcio" />
  <meta property="og:description" content="Rosa, giocatori e statistiche della prima squadra del Rubiana Calcio." />
  <meta property="og:url" content="https://rubianacalcio.it/squadra" />
  <meta property="og:type" content="website" />
  <meta property="og:image" content="https://rubianacalcio.it/logo512.png" />
</Helmet>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 to-zinc-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-overline text-yellow-400 mb-4">// Prima squadra</div>
          <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter uppercase leading-none">
            La rosa<br /><span className="text-yellow-400">gialloblù</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-400">Il roster della stagione {stagione}, lo staff tecnico e l'agenda completa del campionato.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Tabs defaultValue="rosa" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1 h-auto" data-testid="squadra-tabs">
            <TabsTrigger value="rosa" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-zinc-950 px-5 py-2 font-display font-bold uppercase text-xs tracking-wider" data-testid="tab-rosa">Rosa</TabsTrigger>
            <TabsTrigger value="staff" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-zinc-950 px-5 py-2 font-display font-bold uppercase text-xs tracking-wider" data-testid="tab-staff">Staff</TabsTrigger>
            <TabsTrigger value="calendario" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-zinc-950 px-5 py-2 font-display font-bold uppercase text-xs tracking-wider" data-testid="tab-calendario">Calendario</TabsTrigger>
            <TabsTrigger value="risultati" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-zinc-950 px-5 py-2 font-display font-bold uppercase text-xs tracking-wider" data-testid="tab-risultati">Risultati</TabsTrigger>
          </TabsList>

          <TabsContent value="rosa" className="mt-10">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {playersOrdinati.map((p) => {
                return (
                  <div
                    key={p.id}
                    onClick={() => setSelectedPlayer(p)}
                    className="group relative rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 transition-all hover:-translate-y-1 hover:border-yellow-400/50 cursor-pointer"
                    data-testid={`player-${p.id}`}
                  >
                    <div className="aspect-[3/4] overflow-hidden bg-zinc-950">
                      <img src={p.image} alt={p.name} className="h-full w-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                    </div>
                      <div className="p-4">
                      <div className="text-overline text-blue-400 mb-1">{p.role}</div>
                      <div className="font-display font-bold text-sm leading-tight">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-1.5 flex items-center justify-end">
                        {p.goals > 0 && <span className="flex items-center gap-1 text-yellow-400"><Trophy className="h-3 w-3" />{p.goals}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="staff" className="mt-10">
  {(() => {
    // La tua mappa di ordinamento
    const ordineRuoli = {
      "Presidente": 1, "Vice Presidente": 2, "Tesoriere": 3, "Segretaria": 4, "Consigliere": 5, 
      "Allenatore": 1, "Vice Allenatore": 2, "Direttore Sportivo": 3, "Accompagnatore": 4, "Fisioterapista": 5, "Preparatore Atletico": 6
    };

    const staffOrdinato = [...staff].sort((a, b) => (ordineRuoli[a.role] || 99) - (ordineRuoli[b.role] || 99));
    
    const staffSportivo = staffOrdinato.filter(s => s.is_sportivo);
    const staffAmministrativo = staffOrdinato.filter(s => s.is_amministrativo);

    return (
      <div className="space-y-16">
        {/* Sezione Sportiva */}
        {staffSportivo.length > 0 && (
          <div>
            <h3 className="text-xl font-display font-bold text-yellow-400 mb-8 uppercase tracking-wider">// Area Sportiva</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {staffSportivo.map((s) => (
                <div key={s.id} className="rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800">
                  <div className="aspect-square overflow-hidden bg-zinc-950">
                    <img src={s.image} alt={s.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="p-5">
                    <div className="text-overline text-zinc-400 mb-1">{s.role}</div>
                    <div className="font-display font-bold">{s.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sezione Amministrativa */}
        {staffAmministrativo.length > 0 && (
          <div>
            <h3 className="text-xl font-display font-bold text-yellow-400 mb-8 uppercase tracking-wider">// Area Amministrativa</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {staffAmministrativo.map((s) => (
                <div key={s.id} className="rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800">
                  <div className="aspect-square overflow-hidden bg-zinc-950">
                    <img src={s.image} alt={s.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="p-5">
                    <div className="text-overline text-zinc-400 mb-1">{s.role}</div>
                    <div className="font-display font-bold">{s.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  })()}
</TabsContent>

<TabsContent value="calendario" className="mt-10">
            <CalendarioWidget tipo="calcio" compact={false} />
          </TabsContent>

          <TabsContent value="risultati" className="mt-10">
            <div className="space-y-3">
              {played.length === 0 && <p className="text-zinc-500">Nessun risultato.</p>}
              {played.map((m) => {
                const won = (m.home === "ASD Rubiana" && m.home_score > m.away_score) || (m.away === "ASD Rubiana" && m.away_score > m.home_score);
                return (
                  <div key={m.id} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`result-${m.id}`}>
                    <div className="text-xs text-zinc-500 sm:w-32">{new Date(m.date).toLocaleDateString("it-IT")}</div>
                    <div className="flex-1 flex items-center justify-center gap-4">
                      <div className="font-display font-bold text-right flex-1">{m.home}</div>
                      <div className={`font-display font-black text-2xl px-4 py-1 rounded-lg ${won ? "bg-yellow-400 text-zinc-950" : "bg-zinc-800"}`}>{m.home_score}-{m.away_score}</div>
                      <div className="font-display font-bold text-left flex-1">{m.away}</div>
                    </div>
                    <div className="text-xs text-zinc-500 sm:text-right sm:w-44">{m.competition}</div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {selectedPlayer && (
        <PlayerStatsModal player={selectedPlayer} onClose={() => setSelectedPlayer(null)} />
      )}
    </div>
  );
}